import { EditIcon } from '@persona-id/design-tokens/icons';
import { FC, useCallback, useMemo } from 'react';

import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import EditConditionalPanel from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/EditConditionalPanel';
import RulesSection from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/RulesSection';
import TriggerAdvancedConfigurationPanel from 'dashboard/components/WorkflowControlPanel/TriggerAdvancedConfigurationPanel/TriggerAdvancedConfigurationPanel';
import TriggerLiveTest from 'dashboard/components/WorkflowControlPanel/TriggerLiveTest/TriggerLiveTest';
import {
  EMPTY_LOGIC_BLOCK_OR_V2,
  generateJsonSchemaConfig,
  LogicBlockOrV2,
} from 'dashboard/lib/workflows';
import { TransactionType } from 'dashboard/models/transaction-type';
import { SupportedTriggerEvent, TriggerEvent } from 'dashboard/models/trigger';
import { WorkflowError, WorkflowSchema } from 'dashboard/models/workflow';
import { INPUT_STEP_TOKEN, WorkflowConditional } from 'dashboard/models/workflow-version';
import { useUsableObjects } from 'dashboard/redux/selectors/workflow-editor-context';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { EVENT_NAME_TO_DESCRIPTION, EventName, EventType } from 'lib/constants/events';
import { getJsonSchemaForFieldSchema } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchemaCore } from 'lib/json-schema';
import { eventTypeFor, TokensToNode } from 'lib/workflows';

interface ParamsTriggerEventProps {
  trigger: TriggerEvent;
  workflowSchema: WorkflowSchema;
  editable: boolean;
  errors: WorkflowError[];
  events: EventName[];
  tokensToSteps: TokensToNode;
  updateTransactionTemplateFilter: (newFilter: string) => void;
  updateTriggerRules: (newCriteria: LogicBlockOrV2) => void;
  updateTriggerEventName: (newName: SupportedTriggerEvent) => void;
  transactionTypes?: TransactionType[];
  workflowVersionExpiration: number;
  updateWorkflowVersionExpiration: (expiration: number) => void;
}

const ParamsTriggerEvent: FC<ParamsTriggerEventProps> = ({
  editable,
  errors,
  events,
  workflowSchema,
  updateTransactionTemplateFilter,
  updateTriggerRules,
  updateTriggerEventName,
  tokensToSteps,
  trigger,
  transactionTypes,
  workflowVersionExpiration,
  updateWorkflowVersionExpiration,
}) => {
  const usableObjects = useUsableObjects(INPUT_STEP_TOKEN);

  const { jsonSchema, tokensToDisplayValue } = useMemo(
    () =>
      generateJsonSchemaConfig({
        usableObjects,
      }),
    [usableObjects],
  );
  const eventOptions = useMemo(() => {
    return events.map((e) => ({ label: EVENT_NAME_TO_DESCRIPTION[e], value: e }));
  }, [events]);

  const [conditionalPanelOpened, { enable: openConditionalPanel, disable: closeConditionalPanel }] =
    useToggle(false);

  const hasConditions = trigger.attributes.rules !== true;
  const editableRules: LogicBlockOrV2 =
    trigger.attributes.rules === true ? EMPTY_LOGIC_BLOCK_OR_V2 : trigger.attributes.rules;

  const updateTriggerRuleConditions = useCallback(
    (newConditional: WorkflowConditional) => {
      const newOrRules = newConditional.rules;
      updateTriggerRules(newOrRules.or.length > 0 ? newOrRules : (true as any));
    },
    [updateTriggerRules],
  );
  const isTransactionTrigger = eventTypeFor(trigger) === EventType.Transaction;
  const transactionTypeId = isTransactionTrigger
    ? trigger.attributes['template-filters']?.['transaction-type']?.[0] || ''
    : '';
  const transactionType =
    isTransactionTrigger && transactionTypes?.length
      ? transactionTypes.find((tt) => tt.id === transactionTypeId)
      : null;

  const parameterJson: Record<string, JsonSchemaCore> = useMemo(() => {
    if (transactionType == null) {
      return {};
    }

    return transactionType.attributes['field-schemas'].reduce(
      (json, fs) => ({
        ...json,
        ...(fs.config['deactivated-at'] == null
          ? { [fs.key]: getJsonSchemaForFieldSchema(fs) }
          : {}),
      }),
      {},
    );
  }, [transactionType]);

  const renderTxnTypeDropdown = () => {
    if (!isTransactionTrigger || !transactionTypes?.length) {
      return null;
    }

    const transactionTypeOptions = transactionTypes.map((tt) => ({
      label: tt.attributes['name'],
      value: tt.id,
    }));
    return (
      <>
        <Spacer height={2} />
        <ParamLabel
          htmlFor='params-trigger-event__transaction-type-input'
          labelText='Transaction Type'
        />
        <ReactSelect
          id='params-trigger-event__transaction-type'
          value={transactionTypeId}
          onChange={updateTransactionTemplateFilter}
          disabled={!editable}
          options={transactionTypeOptions}
          isSearchable={true}
          portal={true}
        />
      </>
    );
  };

  return (
    <>
      <ParamLabel htmlFor='params-trigger-event__event-type-input' labelText='Event Type' />
      {editable ? (
        <ReactSelect<EventName>
          id='params-trigger-event__event-type'
          value={trigger.attributes['event-name']}
          onChange={updateTriggerEventName}
          options={eventOptions}
          isSearchable={true}
          portal={true}
        />
      ) : (
        <Paragraph>{EVENT_NAME_TO_DESCRIPTION[trigger.attributes['event-name']]}</Paragraph>
      )}
      {renderTxnTypeDropdown()}
      <Spacer height={2} />
      <ParamLabel
        labelText='Trigger Criteria'
        htmlFor='params-trigger-event__trigger-criteria'
        buttons={[
          <Button
            key='edit'
            width='icon'
            variant='basic'
            disabled={!editable}
            onClick={() => {
              if (!hasConditions) {
                updateTriggerRules(EMPTY_LOGIC_BLOCK_OR_V2);
              }
              openConditionalPanel();
            }}
            id='params-trigger-event__trigger-criteria'
          >
            <EditIcon />
          </Button>,
        ]}
      />
      {hasConditions && conditionalPanelOpened && (
        <EditConditionalPanel
          conditional={{
            token: trigger.id,
            name: 'trigger',
            rules: editableRules,
          }}
          panelTitle='Trigger Criteria'
          jsonSchema={jsonSchema}
          tokensToDisplayValue={tokensToDisplayValue}
          workflowSchema={workflowSchema}
          allowEditName={false}
          updateConditional={updateTriggerRuleConditions}
          onClose={closeConditionalPanel}
        />
      )}
      <Spacer height={1} />
      {hasConditions ? (
        <RulesSection
          rules={editableRules}
          usableObjects={usableObjects}
          jsonSchema={jsonSchema}
          tokensToDisplayValue={tokensToDisplayValue}
          tokensToSteps={tokensToSteps}
          routeIndex={0}
          errors={errors}
        />
      ) : (
        <Paragraph>
          No criteria exist. Every <strong>{trigger.attributes['event-name']}</strong> event with
          selected type will trigger this workflow.
        </Paragraph>
      )}
      <Spacer height={3} />
      <TriggerAdvancedConfigurationPanel
        editable={editable}
        workflowVersionExpiration={workflowVersionExpiration}
        updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
      />
      <Spacer height={3} />
      {editable && isTransactionTrigger && (
        <TriggerLiveTest
          apiEndpoint='/api/v1/transactions'
          parameterJson={parameterJson}
          isDirty={false}
          isCurrentPublishedVersion={true}
          transactionTypeId={transactionTypeId}
        />
      )}
    </>
  );
};

export default ParamsTriggerEvent;
